import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { money } from '../events/util';
import {
  ResponsiveContainer, Bar, ComposedChart, Line, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Legend,
} from 'recharts';

const INK = '#9A9A9A';
const RED = '#C8102E', GREEN = '#2ecc71', GRID = '#2A2A2A';
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MESES_C = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const axis = { stroke: INK, fontSize: 11 };
const tooltipStyle = { contentStyle: { background: '#0E0E0E', border: '1px solid #2A2A2A', borderRadius: 4, color: '#fff', fontSize: 12 } };

// Tipos de egreso — con explicación en criollo (el sistema enseña, no asume que el dueño sabe).
const EXP_TYPES = [
  { key: 'operativo', label: 'Operativo', color: '#e67e22', help: 'Gasto real del negocio: mercadería, sueldos, servicios, comisiones, monitoreo. Baja tu ganancia y tu caja.' },
  { key: 'inversion', label: 'Inversión', color: '#3498db', help: 'Mejoras o equipamiento (obra, muebles, equipos). La plata sale pero te queda algo: baja tu caja, NO tu ganancia.' },
  { key: 'deuda', label: 'Deuda / cuota', color: '#9b59b6', help: 'Pago de una deuda o cuota (ej. la llave del boliche). Ya lo compraste antes: baja tu caja, NO tu ganancia.' },
  { key: 'retiro', label: 'Retiro', color: '#d4af37', help: 'Plata que sacás para vos. No es un gasto del negocio: baja tu caja, NO tu ganancia.' },
];
const EXP_MAP = Object.fromEntries(EXP_TYPES.map(t => [t.key, t]));

export default function Contable() {
  const api = useApi();
  const now = new Date();
  const [view, setView] = useState('mensual');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [annual, setAnnual] = useState(null);
  const [nf, setNf] = useState({ concept: '', efectivo: '', digital: '', type: 'operativo' });
  const [ni, setNi] = useState({ concept: '', efectivo: '', digital: '' });

  const load = useCallback(() => {
    api.get(`/api/admin/contable?year=${year}&month=${month}`).then(setData).catch(() => {});
  }, [year, month]); // eslint-disable-line
  const loadAnnual = useCallback(() => {
    setAnnual(null);
    api.get(`/api/admin/contable-anual?year=${year}`).then(setAnnual).catch(() => {});
  }, [year]); // eslint-disable-line
  useEffect(() => { if (view === 'mensual') load(); else loadAnnual(); }, [view, load, loadAnnual]);

  async function addExpense() {
    if (!nf.concept.trim() && !nf.efectivo && !nf.digital) return;
    try {
      await api.post('/api/admin/expenses', { year, month, concept: nf.concept || 'Gasto', efectivo: +nf.efectivo || 0, digital: +nf.digital || 0, type: nf.type });
      setNf({ concept: '', efectivo: '', digital: '', type: nf.type }); load();
    } catch (e) { alert(e.message); }
  }
  async function rmExpense(id) { try { await api.del(`/api/admin/expenses/${id}`); load(); } catch (e) { alert(e.message); } }
  async function setExpenseType(id, type) { try { await api.patch(`/api/admin/expenses/${id}`, { type }); load(); } catch (e) { alert(e.message); } }

  async function addIncome() {
    if (!ni.concept.trim() && !ni.efectivo && !ni.digital) return;
    try {
      await api.post('/api/admin/incomes', { year, month, concept: ni.concept || 'Ingreso', efectivo: +ni.efectivo || 0, digital: +ni.digital || 0 });
      setNi({ concept: '', efectivo: '', digital: '' }); load();
    } catch (e) { alert(e.message); }
  }
  async function rmIncome(id) { try { await api.del(`/api/admin/incomes/${id}`); load(); } catch (e) { alert(e.message); } }

  const years = []; for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 5; y--) years.push(y);
  const t = data?.totals;

  return (
    <div className="lb-fade">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div className="lb-display" style={{ fontSize: 24 }}>Contable</div>
        {view === 'mensual' && (
          <select className="lb-select" style={{ width: 150 }} value={month} onChange={e => setMonth(+e.target.value)}>
            {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        )}
        <select className="lb-select" style={{ width: 110 }} value={year} onChange={e => setYear(+e.target.value)}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div className="lb-subtabs" style={{ marginBottom: 24 }}>
        <button className={view === 'mensual' ? 'active' : ''} onClick={() => setView('mensual')}>Balance mensual</button>
        <button className={view === 'anual' ? 'active' : ''} onClick={() => setView('anual')}>Balance anual</button>
      </div>

      {view === 'mensual' && (!data ? <div style={{ color: INK }}>Cargando…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* 3 cards resumen */}
          <div className="tk-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
            <div className="lb-card" style={{ padding: '18px 20px' }}><div className="lb-eyebrow">Ingresos del mes</div><div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 24, color: '#fff', marginTop: 6 }}>{money(t.ingresoTotal)}</div></div>
            <div className="lb-card" style={{ padding: '18px 20px' }}><div className="lb-eyebrow">Gastos operativos</div><div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 24, color: 'var(--red-400)', marginTop: 6 }}>{money(t.operativoTotal)}</div><div style={{ fontSize: 11, color: INK, marginTop: 3 }}>los que bajan la ganancia</div></div>
            <div className="lb-card" style={{ padding: '18px 20px' }}><div className="lb-eyebrow">Ganancia del mes</div><div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 24, color: t.gananciaTotal >= 0 ? '#2ecc71' : 'var(--red-400)', marginTop: 6 }}>{money(t.gananciaTotal)}</div><div style={{ fontSize: 11, color: INK, marginTop: 3 }}>ingresos − operativos</div></div>
          </div>

          {/* Ingresos por evento */}
          <div>
            <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Ingresos por evento (lo que queda al venue)</div>
            <div className="lb-card tk-scroll-x" style={{ padding: '6px 22px' }}>
              <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 1fr' }}>
                <span>Evento</span><span>Efectivo</span><span>Digital</span><span>Total</span>
              </div>
              {data.events.map(e => (
                <div key={e.id} className="lb-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 1fr' }}>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{e.name}</span>
                  <span style={{ color: INK }}>{money(e.efectivo)}</span>
                  <span style={{ color: INK }}>{money(e.digital)}</span>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{money(e.total)}</span>
                </div>
              ))}
              {data.events.length === 0 && <div style={{ padding: '16px 0', color: INK, fontSize: 13 }}>Sin eventos este mes.</div>}
              <div className="lb-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 1fr', borderTop: '1px dashed rgba(255,255,255,0.16)' }}>
                <span className="lb-eyebrow">Subtotal eventos</span>
                <span style={{ color: '#fff' }}>{money(t.eventoEf)}</span>
                <span style={{ color: '#fff' }}>{money(t.eventoDig)}</span>
                <span style={{ color: '#fff', fontWeight: 700 }}>{money(t.eventoTotal)}</span>
              </div>
            </div>
          </div>

          {/* Ingresos manuales */}
          <div>
            <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Ingresos manuales (anticipos, eventos privados…)</div>
            <div className="lb-card tk-scroll-x" style={{ padding: '6px 22px' }}>
              <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 1fr 28px' }}>
                <span>Concepto</span><span>Efectivo</span><span>Digital</span><span>Total</span><span></span>
              </div>
              {(data.manualIncomes || []).map(g => (
                <div key={g.id} className="lb-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 1fr 28px' }}>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{g.concept}</span>
                  <span style={{ color: '#2ecc71' }}>{money(g.efectivo)}</span>
                  <span style={{ color: '#2ecc71' }}>{money(g.digital)}</span>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{money(g.total)}</span>
                  <button className="lb-iconbtn" onClick={() => rmIncome(g.id)}>✕</button>
                </div>
              ))}
              {(!data.manualIncomes || data.manualIncomes.length === 0) && <div style={{ padding: '14px 0', color: INK, fontSize: 13 }}>Sin ingresos manuales cargados.</div>}
              <div className="lb-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 1fr 28px', borderTop: '1px dashed rgba(255,255,255,0.16)' }}>
                <input className="lb-input" style={{ padding: '9px 11px', fontSize: 13 }} placeholder="Ej: Anticipo mesa 12" value={ni.concept} onChange={e => setNi({ ...ni, concept: e.target.value })} />
                <input className="lb-input" style={{ padding: '9px 11px', fontSize: 13 }} placeholder="Efectivo" inputMode="numeric" value={ni.efectivo} onChange={e => setNi({ ...ni, efectivo: e.target.value.replace(/\D/g, '') })} />
                <input className="lb-input" style={{ padding: '9px 11px', fontSize: 13 }} placeholder="Digital" inputMode="numeric" value={ni.digital} onChange={e => setNi({ ...ni, digital: e.target.value.replace(/\D/g, '') })} />
                <span style={{ color: INK, fontSize: 13 }}>{money((+ni.efectivo || 0) + (+ni.digital || 0))}</span>
                <button className="lb-btn lb-btn--white lb-btn--sm" onClick={addIncome}>+</button>
              </div>
              <div style={{ fontSize: 11, color: INK, marginTop: 6 }}>Evitá poner nombres completos de personas en el concepto.</div>
            </div>
          </div>

          {/* Gastos fijos */}
          <div>
            <div className="lb-eyebrow" style={{ marginBottom: 6 }}>Gastos fijos del mes</div>
            <div style={{ fontSize: 11.5, color: INK, marginBottom: 12, lineHeight: 1.55 }}>
              Clasificá cada egreso. Solo el <b style={{ color: '#fff' }}>operativo</b> baja tu ganancia; inversión, deuda y retiro bajan tu caja pero <b style={{ color: '#fff' }}>no</b> tu ganancia.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '6px 18px', marginBottom: 12 }}>
              {EXP_TYPES.map(x => (
                <div key={x.key} style={{ fontSize: 11, color: INK, display: 'flex', gap: 7, lineHeight: 1.45 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: x.color, flex: 'none', marginTop: 3 }} />
                  <span><b style={{ color: '#fff' }}>{x.label}.</b> {x.help}</span>
                </div>
              ))}
            </div>
            <div className="lb-card tk-scroll-x" style={{ padding: '6px 22px' }}>
              <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.4fr 1.1fr 0.9fr 0.9fr 0.9fr 28px' }}>
                <span>Concepto</span><span>Tipo</span><span>Efectivo</span><span>Digital</span><span>Total</span><span></span>
              </div>
              {data.expenses.map(g => {
                const ty = EXP_MAP[g.type] ? g.type : 'operativo';
                return (
                <div key={g.id} className="lb-row" style={{ gridTemplateColumns: '1.4fr 1.1fr 0.9fr 0.9fr 0.9fr 28px', alignItems: 'center' }}>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{g.concept}</span>
                  <select className="lb-select" style={{ padding: '4px 6px', fontSize: 12, borderLeft: `3px solid ${EXP_MAP[ty].color}` }} value={ty} onChange={e => setExpenseType(g.id, e.target.value)} title={EXP_MAP[ty].help}>
                    {EXP_TYPES.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
                  </select>
                  <span style={{ color: 'var(--red-400)' }}>{money(g.efectivo)}</span>
                  <span style={{ color: 'var(--red-400)' }}>{money(g.digital)}</span>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{money(g.total)}</span>
                  <button className="lb-iconbtn" onClick={() => rmExpense(g.id)}>✕</button>
                </div>
                );
              })}
              {data.expenses.length === 0 && <div style={{ padding: '14px 0', color: INK, fontSize: 13 }}>Sin gastos cargados.</div>}
              <div className="lb-row" style={{ gridTemplateColumns: '1.4fr 1.1fr 0.9fr 0.9fr 0.9fr 28px', borderTop: '1px dashed rgba(255,255,255,0.16)' }}>
                <input className="lb-input" style={{ padding: '9px 11px', fontSize: 13 }} placeholder="Concepto" value={nf.concept} onChange={e => setNf({ ...nf, concept: e.target.value })} />
                <select className="lb-select" style={{ padding: '6px 8px', fontSize: 12, borderLeft: `3px solid ${EXP_MAP[nf.type].color}` }} value={nf.type} onChange={e => setNf({ ...nf, type: e.target.value })}>
                  {EXP_TYPES.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
                </select>
                <input className="lb-input" style={{ padding: '9px 11px', fontSize: 13 }} placeholder="Efectivo" inputMode="numeric" value={nf.efectivo} onChange={e => setNf({ ...nf, efectivo: e.target.value.replace(/\D/g, '') })} />
                <input className="lb-input" style={{ padding: '9px 11px', fontSize: 13 }} placeholder="Digital" inputMode="numeric" value={nf.digital} onChange={e => setNf({ ...nf, digital: e.target.value.replace(/\D/g, '') })} />
                <span style={{ color: INK, fontSize: 13 }}>{money((+nf.efectivo || 0) + (+nf.digital || 0))}</span>
                <button className="lb-btn lb-btn--white lb-btn--sm" onClick={addExpense}>+</button>
              </div>
              <div style={{ fontSize: 11, color: INK, marginTop: 6 }}>Evitá poner nombres completos de personas en el concepto.</div>
              <div style={{ padding: '8px 0 4px', fontSize: 11.5, lineHeight: 1.45 }}>
                <b style={{ color: EXP_MAP[nf.type].color }}>{EXP_MAP[nf.type].label}:</b> <span style={{ color: INK }}>{EXP_MAP[nf.type].help}</span>
              </div>
            </div>
          </div>

          {/* Ganancia y Caja — las dos cifras separadas */}
          <div className="lb-card" style={{ padding: '24px 26px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }} className="tk-2col">
              <div>
                <div className="lb-eyebrow">Ganancia de {MESES[month - 1]} {year}</div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 34, color: t.gananciaTotal >= 0 ? '#2ecc71' : 'var(--red-400)', marginTop: 6 }}>{money(t.gananciaTotal)}</div>
                <div style={{ fontSize: 12, color: INK, marginTop: 4, lineHeight: 1.5 }}>¿El boliche es rentable?<br />Ingresos − gastos operativos.</div>
              </div>
              <div style={{ borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: 22 }}>
                <div className="lb-eyebrow">Caja</div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 34, color: t.cajaTotal >= 0 ? '#fff' : 'var(--red-400)', marginTop: 6 }}>{money(t.cajaTotal)}</div>
                <div style={{ fontSize: 12, color: INK, marginTop: 4, lineHeight: 1.5 }}>¿Cuánta plata tenés?<br />Ingresos − todos los egresos.</div>
                <div style={{ display: 'flex', gap: 20, marginTop: 12, flexWrap: 'wrap' }}>
                  <div><div style={{ fontSize: 11, color: INK }}>En mano (efectivo)</div><div style={{ fontWeight: 700, color: t.cajaEf >= 0 ? '#fff' : 'var(--red-400)' }}>{money(t.cajaEf)}</div></div>
                  <div><div style={{ fontSize: 11, color: INK }}>En cuenta (digital)</div><div style={{ fontWeight: 700, color: t.cajaDig >= 0 ? '#fff' : 'var(--red-400)' }}>{money(t.cajaDig)}</div></div>
                </div>
              </div>
            </div>
            {(t.inversionTotal > 0 || t.deudaTotal > 0 || t.retiroTotal > 0) && (
              <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ fontSize: 12, color: INK, marginBottom: 10 }}>La diferencia entre ganancia y caja (<b style={{ color: '#fff' }}>{money(t.gananciaTotal - t.cajaTotal)}</b>) se fue a:</div>
                <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
                  {[['Inversión', t.inversionTotal, EXP_MAP.inversion.color], ['Deuda / cuota', t.deudaTotal, EXP_MAP.deuda.color], ['Retiro', t.retiroTotal, EXP_MAP.retiro.color]].map(([lbl, val, col]) => (
                    <div key={lbl}>
                      <div style={{ fontSize: 11, color: INK, display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: col }} />{lbl}</div>
                      <div style={{ fontWeight: 700, color: '#fff', marginTop: 2 }}>{money(val)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      {view === 'anual' && (!annual ? <div style={{ color: INK }}>Cargando…</div> : (
        <Anual annual={annual} year={year} />
      ))}
    </div>
  );
}

function Anual({ annual, year }) {
  const a = annual.totals;
  const chart = annual.months.map(m => ({ mes: MESES_C[m.month - 1], ingreso: m.ingreso, egreso: m.egreso, ganancia: m.ganancia, caja: m.caja }));
  const mejor = a.mejorMes ? MESES[a.mejorMes - 1] : '—';
  return (
    <div className="lb-fade" style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
      {/* KPIs del año */}
      <div className="tk-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        <Kpi label={`Ingresos ${year}`} value={money(a.ingresoTotal)} color="#fff" />
        <Kpi label="Ganancia anual" value={money(a.gananciaTotal)} color={a.gananciaTotal >= 0 ? '#2ecc71' : 'var(--red-400)'} sub="ingresos − operativos" />
        <Kpi label="Caja anual" value={money(a.cajaTotal)} color={a.cajaTotal >= 0 ? '#fff' : 'var(--red-400)'} sub="ingresos − todos los egresos" />
        <Kpi label="Eventos / mejor mes" value={String(a.eventos)} color="#fff" sub={`Mejor: ${mejor}`} />
      </div>

      {/* Ganancia por mes (con caja de referencia) */}
      <div>
        <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Ganancia por mes <span style={{ color: INK, textTransform: 'none', fontWeight: 400 }}>· la línea blanca es la caja</span></div>
        <div className="lb-card" style={{ padding: '18px 14px 8px' }}>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chart}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="mes" {...axis} />
              <YAxis {...axis} tickFormatter={v => money(v)} width={84} />
              <Tooltip {...tooltipStyle} cursor={{ fill: 'rgba(255,255,255,0.04)' }} formatter={(v, n) => [money(v), n === 'ganancia' ? 'Ganancia' : 'Caja']} />
              <Legend wrapperStyle={{ fontSize: 12 }} formatter={n => n === 'ganancia' ? 'Ganancia (rentabilidad)' : 'Caja (plata)'} />
              <ReferenceLine y={0} stroke={INK} />
              <Bar dataKey="ganancia" radius={[3, 3, 0, 0]}>
                {chart.map((m, i) => <Cell key={i} fill={m.ganancia >= 0 ? GREEN : RED} />)}
              </Bar>
              <Line type="monotone" dataKey="caja" stroke="#fff" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Ingresos vs egresos por mes */}
      <div>
        <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Ingresos vs egresos por mes</div>
        <div className="lb-card" style={{ padding: '18px 14px 8px' }}>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chart}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="mes" {...axis} />
              <YAxis {...axis} tickFormatter={v => money(v)} width={84} />
              <Tooltip {...tooltipStyle} cursor={{ fill: 'rgba(255,255,255,0.04)' }} formatter={(v, n) => [money(v), n === 'ingreso' ? 'Ingresos' : n === 'egreso' ? 'Egresos' : 'Ganancia']} />
              <Legend wrapperStyle={{ fontSize: 12 }} formatter={n => n === 'ingreso' ? 'Ingresos' : n === 'egreso' ? 'Egresos' : 'Ganancia'} />
              <Bar dataKey="ingreso" fill={GREEN} radius={[3, 3, 0, 0]} />
              <Bar dataKey="egreso" fill={RED} radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="ganancia" stroke="#fff" strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detalle mensual */}
      <div>
        <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Detalle mes a mes</div>
        <div className="lb-card tk-scroll-x" style={{ padding: '6px 22px' }}>
          <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.2fr 0.7fr 1fr 1fr 1fr' }}>
            <span>Mes</span><span>Eventos</span><span>Ingresos</span><span>Ganancia</span><span>Caja</span>
          </div>
          {annual.months.map(m => (
            <div key={m.month} className="lb-row" style={{ gridTemplateColumns: '1.2fr 0.7fr 1fr 1fr 1fr' }}>
              <span style={{ color: '#fff', fontWeight: 600 }}>{MESES[m.month - 1]}</span>
              <span style={{ color: INK }}>{m.eventos}</span>
              <span style={{ color: INK }}>{money(m.ingreso)}</span>
              <span style={{ color: m.ganancia >= 0 ? '#2ecc71' : 'var(--red-400)', fontWeight: 600 }}>{money(m.ganancia)}</span>
              <span style={{ color: m.caja >= 0 ? '#fff' : 'var(--red-400)', fontWeight: 600 }}>{money(m.caja)}</span>
            </div>
          ))}
          <div className="lb-row" style={{ gridTemplateColumns: '1.2fr 0.7fr 1fr 1fr 1fr', borderTop: '1px dashed rgba(255,255,255,0.16)' }}>
            <span className="lb-eyebrow">Total {year}</span>
            <span style={{ color: '#fff' }}>{a.eventos}</span>
            <span style={{ color: '#fff' }}>{money(a.ingresoTotal)}</span>
            <span style={{ color: a.gananciaTotal >= 0 ? '#2ecc71' : 'var(--red-400)', fontWeight: 700 }}>{money(a.gananciaTotal)}</span>
            <span style={{ color: a.cajaTotal >= 0 ? '#fff' : 'var(--red-400)', fontWeight: 700 }}>{money(a.cajaTotal)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, color, sub }) {
  return (
    <div className="lb-card" style={{ padding: '18px 20px' }}>
      <div className="lb-eyebrow">{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 24, color, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: INK, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

